import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { createAnalyticsDeviceInfo } from '@shared/utils/analytics';
import type { AnalyticsViewingEvent } from '@shared/types/analytics';
import type { GalleryUser } from '@shared/types/auth';

type ViewingEventType = AnalyticsViewingEvent['event_type'];

interface CallableResponse {
  success?: boolean;
  message?: string;
  sessionId?: string;
  isFavorite?: boolean;
}

interface StartSessionPayload {
  sessionId: string;
  userId: string;
  galleryId: string;
  collectionId: string;
  isGuided: boolean;
  deviceInfo: Record<string, string>;
}

interface EndSessionPayload {
  sessionId: string;
  galleryId: string;
  durationMs: number;
}

interface RecordViewingEventPayload {
  sessionId: string;
  galleryId: string;
  collectionId: string;
  userId: string;
  pieceId: string;
  eventType: ViewingEventType;
  durationMs: number;
}

interface ToggleFavoritePiecePayload {
  galleryId: string;
  userId: string;
  pieceId: string;
  sessionId?: string;
  collectionId: string;
  isFavorite: boolean;
}

interface CurrentPieceView {
  galleryId: string;
  collectionId: string;
  pieceId: string;
  sessionId: string;
  userId: string;
  startedAtMs: number;
}

interface AnalyticsState {
  currentPieceView: CurrentPieceView | null;
  sessionStartTime: number;
  lastActivityTime: number;
}

const CALLABLE_NAMES = {
  startSession: 'startSession',
  endSession: 'endSession',
  recordViewingEvent: 'recordViewingEvent',
  toggleFavoritePiece: 'toggleFavoritePiece'
} as const;

class AnalyticsService {
  private state: AnalyticsState = {
    currentPieceView: null,
    sessionStartTime: 0,
    lastActivityTime: 0
  };

  private endingSessionIds = new Set<string>();

  /**
   * Start analytics session - matches Unity client's StartAnalyticsSession.
   */
  async startSession(user: GalleryUser, galleryId: string, sessionId: string): Promise<void> {
    const userId = this.getUserId(user);
    const collectionId = user.collectionId;

    if (!userId || !galleryId || !sessionId || !collectionId) {
      console.warn('[Analytics] Skipping startSession; missing required session data.');
      return;
    }

    const deviceInfo = createAnalyticsDeviceInfo();

    await this.callFunction<StartSessionPayload>(
      CALLABLE_NAMES.startSession,
      {
        sessionId,
        userId,
        galleryId,
        collectionId,
        isGuided: false,
        deviceInfo: {
          deviceType: deviceInfo.deviceType || 'unknown',
          device_type: deviceInfo.deviceType || 'unknown',
          deviceModel: deviceInfo.deviceModel || 'Browser',
          device_model: deviceInfo.deviceModel || 'Browser',
          browser: deviceInfo.browser || 'unknown',
          browserVersion: deviceInfo.browserVersion || 'Unknown',
          browser_version: deviceInfo.browserVersion || 'Unknown',
          operatingSystem: deviceInfo.operatingSystem || deviceInfo.platform || 'unknown',
          operating_system: deviceInfo.operatingSystem || deviceInfo.platform || 'unknown',
          os: deviceInfo.operatingSystem || deviceInfo.platform || 'unknown',
          platform: deviceInfo.platform || deviceInfo.operatingSystem || 'unknown',
          screenResolution: deviceInfo.screenResolution || 'unknown',
          screen_resolution: deviceInfo.screenResolution || 'unknown'
        }
      },
      'start session'
    );

    this.state.sessionStartTime = Date.now();
    this.state.lastActivityTime = Date.now();
  }

  /**
   * Record piece view start locally. The callable receives the finalized view
   * duration when the piece changes, unmounts, or the session ends.
   */
  async recordPieceView(
    user: GalleryUser,
    galleryId: string,
    collectionId: string,
    pieceId: string,
    sessionId: string
  ): Promise<void> {
    const userId = this.getUserId(user);

    if (!userId || !galleryId || !collectionId || !pieceId || !sessionId) {
      console.warn('[Analytics] Skipping piece view; missing required event data.');
      return;
    }

    const currentView = this.state.currentPieceView;
    if (currentView) {
      if (currentView.pieceId === pieceId && currentView.sessionId === sessionId) {
        return;
      }

      await this.finalizeCurrentPieceView();
    }

    this.state.currentPieceView = {
      galleryId,
      collectionId,
      pieceId,
      sessionId,
      userId,
      startedAtMs: Date.now()
    };
    this.recordActivity();
  }

  /**
   * Record interaction event (zoom, description view, frame actions, etc.).
   */
  async recordInteractionEvent(
    user: GalleryUser,
    galleryId: string,
    collectionId: string,
    pieceId: string,
    sessionId: string,
    eventType: ViewingEventType
  ): Promise<void> {
    const userId = this.getUserId(user);

    if (!userId || !galleryId || !collectionId || !pieceId || !sessionId) {
      console.warn('[Analytics] Skipping interaction event; missing required event data.');
      return;
    }

    await this.recordViewingEvent({
      sessionId,
      galleryId,
      collectionId,
      userId,
      pieceId,
      eventType: this.toCallableEventType(eventType),
      durationMs: 0
    });
    this.recordActivity();
  }

  /**
   * Toggle favorite status. Returns the backend-confirmed favorite state when available.
   */
  async toggleFavorite(
    user: GalleryUser,
    galleryId: string,
    collectionId: string,
    pieceId: string,
    sessionId: string,
    isFavorite: boolean
  ): Promise<boolean | null> {
    const userId = this.getUserId(user);

    if (!userId || !galleryId || !collectionId || !pieceId) {
      console.warn('[Analytics] Skipping favorite toggle; missing required favorite data.');
      return null;
    }

    const result = await this.callFunction<ToggleFavoritePiecePayload>(
      CALLABLE_NAMES.toggleFavoritePiece,
      {
        galleryId,
        userId,
        pieceId,
        sessionId,
        collectionId,
        isFavorite
      },
      'toggle favorite'
    );

    this.recordActivity();

    if (typeof result?.isFavorite === 'boolean') {
      return result.isFavorite;
    }

    return result?.success ? isFavorite : null;
  }

  /**
   * End current piece view and record duration.
   */
  async finalizeCurrentPieceView(): Promise<void> {
    const currentView = this.state.currentPieceView;
    if (!currentView) return;

    this.state.currentPieceView = null;

    const durationMs = Math.max(0, Date.now() - currentView.startedAtMs);
    await this.recordViewingEvent({
      sessionId: currentView.sessionId,
      galleryId: currentView.galleryId,
      collectionId: currentView.collectionId,
      userId: currentView.userId,
      pieceId: currentView.pieceId,
      eventType: 'view',
      durationMs
    });
  }

  /**
   * End analytics session.
   */
  async endSession(_user: GalleryUser, galleryId: string, sessionId: string): Promise<void> {
    if (!sessionId || this.endingSessionIds.has(sessionId)) return;

    this.endingSessionIds.add(sessionId);

    try {
      await this.finalizeCurrentPieceView();
      await this.callFunction<EndSessionPayload>(
        CALLABLE_NAMES.endSession,
        {
          sessionId,
          galleryId,
          durationMs: this.state.sessionStartTime > 0 ? Date.now() - this.state.sessionStartTime : 0
        },
        'end session'
      );
    } finally {
      this.endingSessionIds.delete(sessionId);
      this.state = {
        currentPieceView: null,
        sessionStartTime: 0,
        lastActivityTime: 0
      };
    }
  }

  /**
   * Record activity to update last activity time.
   */
  recordActivity(): void {
    this.state.lastActivityTime = Date.now();
  }

  private async recordViewingEvent(payload: RecordViewingEventPayload): Promise<void> {
    await this.callFunction<RecordViewingEventPayload>(
      CALLABLE_NAMES.recordViewingEvent,
      payload,
      `record ${payload.eventType} event`
    );
  }

  private async callFunction<Payload>(
    name: (typeof CALLABLE_NAMES)[keyof typeof CALLABLE_NAMES],
    payload: Payload,
    action: string
  ): Promise<CallableResponse | null> {
    try {
      if (!functions) {
        throw new Error('Firebase Functions is not initialized');
      }

      const callable = httpsCallable<Payload, CallableResponse>(functions, name);
      const result = await callable(payload);
      return result.data;
    } catch (error) {
      console.error(`[Analytics] Failed to ${action}:`, error);
      return null;
    }
  }

  private toCallableEventType(eventType: ViewingEventType): ViewingEventType {
    return eventType;
  }

  private getUserId(user: GalleryUser): string | undefined {
    return user.userId || user.id;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;
